<!-- Main Component Wrapper - Max Width constrained to behave like the screenshot component -->
<div class="w-full bg-white border border-[#e5e7eb] relative text-slate-900 px-4 h-full">

    <div class="flex items-center justify-between pt-5 pb-0 border-b border-gray-200 mb-2">
        <h1 class="text-base font-bold text-gray-900 m-0">Request Details</h1>
        <button
            class="bg-white outline-none border-none !bg-transparent hover:bg-gray-50 text-gray-400 hover:text-gray-600 !w-8 !h-8 !rounded-md cursor-pointer"
            (click)="closeDrawer()">
            <span class="material-symbols-outlined !text-lg">close</span>
        </button>
    </div>

    <!-- Spinner -->
    <div *ngIf="isLoading" class="py-6 px-4 flex flex-col items-center w-full">
        <ngx-skeleton-loader count="6" style="width: 100%"
            [theme]="{ 'border-radius': '6px', height: '50px', 'margin-bottom': '0px'}">
        </ngx-skeleton-loader>
    </div>

    <!-- Empty State -->
    <div *ngIf="!isLoading && !requestDetails && requestId" class="p-6 flex flex-col items-center w-full">
        <p class="text-sm text-center text-gray-500">Failed to load request details.</p>
    </div>

    <div *ngIf="!isLoading && requestDetails" class="mt-0">
        <div class="flex-1 pb-20">
            <div class="grid grid-cols-2 gap-y-6 gap-x-2 mb-10">
                <!-- User / Team -->
                <div>
                    <p class="text-[13px] text-[#808080] mb-2 leading-none font-medium">
                        {{ requestDetails.request_type === 'Team' ? 'Team' : 'User' }}
                    </p>
                    <div class="flex items-center gap-[6px] text-[16px] font-normal text-[#1a1a1a]">
                        <span class="material-symbols-outlined text-lg text-[#4a4a51] leading-none mb-px">
                            {{ requestDetails.request_type === 'Team' ? 'groups' : 'person_outline' }}
                        </span>
                        <span class="leading-none text-[15px] font-medium text-gray-800">
                            {{ requestDetails.request_type === 'Team' ? getTeamName(requestDetails.team_id) : 
                               (requestDetails.employee_id ? (requestDetails.employee_name || 'Unknown User') : 'Entire Organization') }}
                        </span>
                    </div>
                </div>

                <!-- Action -->
                <div class="pl-2">
                    <p class="text-[13px] text-[#808080] mb-2 leading-none font-medium">Action</p>
                    <div class="flex items-center gap-[6px] text-[16px] font-normal text-[#1a1a1a]">
                        <span class="material-symbols-outlined text-lg text-[#5459d1] leading-none mb-px"
                            *ngIf="requestDetails.action == 'Download'">download</span>
                        <span class="material-symbols-outlined text-lg text-[#8b5cf6] leading-none mb-px"
                            *ngIf="isRollupRefreshRequest()">insights</span>
                        <span class="material-symbols-outlined text-lg text-[#ef4444] leading-none mb-px"
                            *ngIf="requestDetails.action == 'Delete' && !isRollupRefreshRequest()">delete</span>
                        <span class="leading-none text-[15px] font-medium text-gray-800">{{
                            actionSummaryLabel() }}</span>
                    </div>
                </div>

                <!-- Range -->
                <div class="mt-1">
                    <p class="text-[13px] text-[#808080] mb-2 leading-none font-medium">{{ rangeHeaderLabel() }}</p>
                    <span class="leading-none text-[14px] whitespace-nowrap font-medium text-gray-800">{{
                        rangeDisplayValue() }}</span>
                </div>

                <div class="mt-1 pl-2">
                    <p class="text-[13px] text-[#808080] mb-2 leading-none font-medium">Status</p>
                    <span class="px-[12px] py-[5px] rounded-full text-[13px] font-medium"
                        [ngClass]="requestDetails.status | statusClass">
                        {{ requestDetails.status }}
                    </span>
                </div>
            </div>

            <!-- Job Lifecycle Title -->
            <h1 class="text-[15px] font-bold text-gray-900 m-0 mb-6 pt-6 border-t border-gray-200">Job Lifecycle</h1>

            <!-- Timeline Flow container -->
            <div class="flex flex-col relative space-y-5 gap-0">
                <!-- Timeline Event 1: Request -->
                <div class="flex items-start gap-4">
                    <div class="flex flex-col items-center w-[30px] shrink-0 relative pt-[3px]">
                        <div
                            class="w-8 h-8 bg-[#E3DFED] rounded-full flex items-center justify-center z-10 shrink-0 mb-[6px]">
                            <span class="material-symbols-outlined !text-[15px] text-[#8b5cf6] relative">send</span>
                        </div>
                    </div>
                    <div class="flex flex-col items-start gap-1 mt-0">
                        <p class="!text-[14px] font-medium text-gray-900 leading-tight m-0">Requested Event</p>
                        <p class="!text-[13px] text-[#808080] m-0">{{ formatTime(requestDetails.created_at) }}</p>
                    </div>
                </div>

                <!-- Processing Layer -->
                <div class="flex items-start gap-4"
                    *ngIf="requestDetails.status !== 'Pending' && requestDetails.status !== 'Queued'">
                    <div class="flex flex-col items-center w-[30px] shrink-0 relative pt-[3px]">
                        <div
                            class="w-8 h-8 bg-[#EAE1DA] rounded-full flex items-center justify-center z-10 shrink-0 mb-[6px]">
                            <span class="material-symbols-outlined !text-[15px] text-[#F59E0B] relative">settings</span>
                        </div>
                    </div>
                    <div class="flex flex-col items-start gap-1">
                        <p class="!text-[14px] font-medium text-gray-900 leading-tight m-0">Processing Server Job</p>
                        <p class="!text-[13px] text-[#808080] m-0" *ngIf="requestDetails?.updated_at">
                            {{ formatTime(requestDetails?.updated_at) }}
                        </p>
                    </div>
                </div>

                <!-- Finished Layer -->
                <div class="flex items-start gap-4" *ngIf="requestDetails.status === 'Completed'">
                    <div class="flex flex-col items-center w-[30px] shrink-0 relative pt-[3px]">
                        <div
                            class="w-8 h-8 bg-[#dcf9e9] rounded-full flex items-center justify-center z-10 shrink-0 mb-[6px]">
                            <span
                                class="material-symbols-outlined !text-[15px] text-[#10B981] relative">check_circle</span>
                        </div>
                    </div>
                    <div class="flex flex-col items-start gap-1">
                        <p class="!text-[14px] font-medium text-gray-900 leading-tight m-0">Completed Successfully</p>
                        <p class="!text-[13px] text-[#808080] m-0" *ngIf="requestDetails.updated_at">
                            {{ formatTime(requestDetails.updated_at) }}
                        </p>
                    </div>
                </div>

                <div class="flex items-start gap-4" *ngIf="requestDetails.status === 'Failed'">
                    <div class="flex flex-col items-center w-[30px] shrink-0 relative pt-[3px]">
                        <div
                            class="w-8 h-8 bg-[#ffe4e4] rounded-full flex items-center justify-center z-10 shrink-0 mb-[6px]">
                            <span class="material-symbols-outlined !text-[15px] text-[#ff0000] relative">error</span>
                        </div>
                    </div>
                    <div class="flex flex-col items-start gap-1">
                        <p class="!text-[14px] font-medium text-gray-900 leading-tight m-0">Process Failed</p>
                        <p class="!text-[13px] text-[#808080] m-0 w-full max-w-[250px]">
                            An unexpected error occurred. Please try again.
                        </p>
                    </div>
                </div>

                <div class="flex items-start gap-4" *ngIf="requestDetails.status === 'Cancelled'">
                    <div class="flex flex-col items-center w-[30px] shrink-0 relative pt-[3px]">
                        <div
                            class="w-8 h-8 bg-[#ffe4e4] rounded-full flex items-center justify-center z-10 shrink-0 mb-[6px]">
                            <span class="material-symbols-outlined !text-[15px] text-[#ff0000] relative">close</span>
                        </div>
                    </div>
                    <div class="flex flex-col items-start gap-1">
                        <p class="!text-[14px] font-medium text-gray-900 leading-tight m-0">Request Cancelled</p>
                        <p class="!text-[13px] text-[#808080] m-0" *ngIf="requestDetails.updated_at">
                            {{ formatTime(requestDetails.updated_at) }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Download Action CTA -->
            <div class="mt-12"
                *ngIf="requestDetails.action == 'Download' && requestDetails.status == 'Completed' && requestDetails.download_link">

                <!-- Case: Link Available -->
                <div *ngIf="requestDetails.download_link !== 'NO_SCREENSHOT_AVAILABLE' && requestDetails.download_link !== 'NO_LOGS_AVAILABLE'"
                    class="border-[0.5px] border-[#d8d8d9] rounded-xl bg-[#f6f6f7] py-4 px-[18px] flex flex-col items-center">
                    <!-- Active link -->
                    <a *ngIf="!isDownloadLinkExpired"
                        [href]="requestDetails.download_link"
                        target="_blank"
                        class="flex !justify-center !items-center gap-2 rounded-lg h-[40px] w-full m-0 cursor-pointer transition-all hover:bg-[#4348b6] font-sans no-underline bg-[#5459d1] text-white !text-[15px] font-medium">
                        <span class="material-symbols-outlined text-[20px] text-white">download</span>
                        <span>Download Zip</span>
                    </a>

                    <!-- Expired state -->
                    <button *ngIf="isDownloadLinkExpired"
                        type="button"
                        disabled
                        class="flex !justify-center !items-center gap-2 rounded-lg h-[40px] w-full m-0 transition-all font-sans no-underline bg-gray-400 text-white !text-[15px] font-medium opacity-80 cursor-not-allowed">
                        <span class="material-symbols-outlined text-[20px] text-white">schedule</span>
                        <span>Link expired</span>
                    </button>

                    <p class="text-[13px] text-[#808080] tracking-[0.1px] font-normal leading-none pt-px mt-4 mb-0 text-center w-full"
                        *ngIf="requestDetails?.link_expiring_at">
                        Link expires on {{ formatTime(requestDetails?.link_expiring_at) }}
                    </p>

                    <p class="text-[13px] text-amber-700 tracking-[0.1px] font-normal leading-none pt-px mt-4 mb-0 text-center w-full"
                        *ngIf="requestDetails?.link_expiring_at && isDownloadLinkExpired">
                        This download link has expired. 
                    </p>
                    
                    
                </div>

                <!-- Case: No Data Found -->
                <div *ngIf="requestDetails.download_link === 'NO_SCREENSHOT_AVAILABLE' || requestDetails.download_link === 'NO_LOGS_AVAILABLE'"
                    class="border-[0.5px] border-amber-200 rounded-xl bg-amber-50 py-4 px-[18px] flex flex-col items-center shadow-sm">
                    <div class="flex items-center gap-2 text-amber-700 font-medium">
                        <span class="material-symbols-outlined text-[21px]">info</span>
                        <span class="text-[14px]">
                            {{
                            requestDetails.download_link === 'NO_LOGS_AVAILABLE' ?
                            'No activity logs found for this request' :
                            'No screenshots available for this request'
                            }}
                        </span>
                    </div>
                </div>
            </div>

            <!-- PT rollup summary -->
            <div class="mt-12" *ngIf="isRollupRefreshRequest()">
                <div class="border-[0.5px] border-[#d8d8d9] rounded-xl bg-[#f6f6f7] py-4 px-[18px]">
                    <div class="flex items-center gap-2 font-medium text-[#5459d1] mb-3">
                        <span class="material-symbols-outlined text-[21px]">insights</span>
                        <span class="text-[14px]">Productivity Trends rollup</span>
                    </div>
                    <dl class="grid grid-cols-2 gap-x-4 gap-y-3 m-0 text-[13px]">
                        <div>
                            <dt class="text-[#808080] font-medium m-0">Mode</dt>
                            <dd class="text-gray-800 font-medium m-0 mt-1">{{ rollupModeLabel() }}</dd>
                        </div>
                        <div>
                            <dt class="text-[#808080] font-medium m-0">Scope</dt>
                            <dd class="text-gray-800 font-medium m-0 mt-1">Employee-day aggregates</dd>
                        </div>
                        <div *ngIf="rollupJobSummary()?.days_built != null">
                            <dt class="text-[#808080] font-medium m-0">Days rebuilt</dt>
                            <dd class="text-gray-800 font-medium m-0 mt-1">{{ formatMetric(rollupJobSummary()?.days_built) }}</dd>
                        </div>
                        <div *ngIf="rollupJobSummary()?.rows_affected != null">
                            <dt class="text-[#808080] font-medium m-0">Rows updated</dt>
                            <dd class="text-gray-800 font-medium m-0 mt-1">{{ formatMetric(rollupJobSummary()?.rows_affected) }}</dd>
                        </div>
                        <div *ngIf="rollupJobSummary()?.last_built_date">
                            <dt class="text-[#808080] font-medium m-0">Data through</dt>
                            <dd class="text-gray-800 font-medium m-0 mt-1">{{ formatDate(rollupJobSummary()?.last_built_date) }}</dd>
                        </div>
                        <div *ngIf="rollupJobSummary()?.max_log_id != null">
                            <dt class="text-[#808080] font-medium m-0">Max log id processed</dt>
                            <dd class="text-gray-800 font-medium m-0 mt-1">{{ formatMetric(rollupJobSummary()?.max_log_id) }}</dd>
                        </div>
                    </dl>
                    <p class="text-[13px] text-[#808080] mt-4 mb-0 leading-snug">{{ rollupRebuildHint() }}</p>
                    <div class="mt-4 flex items-center gap-2 font-medium"
                        [ngClass]="requestDetails.status === 'Completed' ? 'text-emerald-700' : (requestDetails.status === 'Failed' ? 'text-red-700' : 'text-amber-700')">
                        <span class="material-symbols-outlined text-[21px]" *ngIf="requestDetails.status === 'Completed'">check_circle</span>
                        <span class="material-symbols-outlined text-[21px]" *ngIf="requestDetails.status === 'Failed'">error</span>
                        <span class="material-symbols-outlined text-[21px]" *ngIf="requestDetails.status !== 'Completed' && requestDetails.status !== 'Failed'">hourglass_top</span>
                        <span class="text-[14px]" *ngIf="requestDetails.status === 'Completed'">Rollup refresh completed.</span>
                        <span class="text-[14px]" *ngIf="requestDetails.status === 'Failed'">Rollup refresh failed. Check datamgmt logs and watermark status.</span>
                        <span class="text-[14px]" *ngIf="requestDetails.status !== 'Completed' && requestDetails.status !== 'Failed'">Rollup refresh in progress…</span>
                    </div>
                </div>
            </div>

            <!-- Delete Action Status (no CTA needed) -->
            <div class="mt-12" *ngIf="requestDetails.action == 'Delete' && !isRollupRefreshRequest()">
                <div class="border-[0.5px] border-[#d8d8d9] rounded-xl bg-[#f6f6f7] py-4 px-[18px] flex flex-col items-center">
                    <div class="flex items-center gap-2 font-medium"
                        [ngClass]="requestDetails.status === 'Completed' ? 'text-emerald-700' : (requestDetails.status === 'Failed' ? 'text-red-700' : 'text-amber-700')">
                        <span class="material-symbols-outlined text-[21px]" *ngIf="requestDetails.status === 'Completed'">check_circle</span>
                        <span class="material-symbols-outlined text-[21px]" *ngIf="requestDetails.status === 'Failed'">error</span>
                        <span class="material-symbols-outlined text-[21px]" *ngIf="requestDetails.status !== 'Completed' && requestDetails.status !== 'Failed'">hourglass_top</span>
                        <span class="text-[14px]" *ngIf="requestDetails.status === 'Completed'">Deletion completed successfully.</span>
                        <span class="text-[14px]" *ngIf="requestDetails.status === 'Failed'">Deletion failed. Please create a new request.</span>
                        <span class="text-[14px]" *ngIf="requestDetails.status !== 'Completed' && requestDetails.status !== 'Failed'">Deletion request is in progress.</span>
                    </div>
                </div>
            </div>

        </div>
    </div>
</div>